import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal, SectionHeading } from "./Reveal";
import { Gauge, BookOpenCheck, Building2, Sigma } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const YEARS = ["2023", "2024", "2026", "2028", "2031"];
const FACT_ICONS = [BookOpenCheck, Building2];
const SECTOR_RISK = [40, 34, 32, 28, 24];
const POSTURE = [
  { id: "cbom", gain: 22 },
  { id: "hsm", gain: 16 },
  { id: "pilot", gain: 22 },
];
const QDAY = new Date("2030-01-01T00:00:00+05:30").getTime();

const MoscaWidget = () => {
  const { t } = useLang();
  const [x, setX] = useState(7);
  const [y, setY] = useState(4);
  const z = Math.max(0, (QDAY - Date.now()) / 31557600000);
  const sum = x + y;
  const atRisk = sum > z;

  return (
    <div data-testid="mosca-calculator" className="p-7 sm:p-8 rounded-3xl border border-amber-200 bg-gradient-to-b from-amber-50/70 to-white shadow-[0_20px_50px_-24px_rgba(217,119,6,0.35)]">
      <div className="flex items-center gap-3">
        <Sigma className="w-5 h-5 text-amber-600" />
        <h3 className="text-lg font-extrabold text-slate-900 tracking-tight">{t("nqm.mosca.title")}</h3>
      </div>
      <p className="mt-2 text-sm text-slate-500 leading-relaxed">{t("nqm.mosca.desc")}</p>

      <div className="mt-6 space-y-5">
        <div>
          <div className="flex justify-between items-baseline">
            <label className="text-[11px] font-mono font-bold uppercase tracking-[0.14em] text-slate-500">{t("nqm.mosca.xLabel")}</label>
            <span className="font-mono font-extrabold text-slate-900">{x}y</span>
          </div>
          <input
            data-testid="mosca-x-slider"
            type="range" min="1" max="15" value={x}
            onChange={(e) => setX(Number(e.target.value))}
            className="mt-2 w-full accent-amber-600"
          />
        </div>
        <div>
          <div className="flex justify-between items-baseline">
            <label className="text-[11px] font-mono font-bold uppercase tracking-[0.14em] text-slate-500">{t("nqm.mosca.yLabel")}</label>
            <span className="font-mono font-extrabold text-slate-900">{y}y</span>
          </div>
          <input
            data-testid="mosca-y-slider"
            type="range" min="1" max="10" value={y}
            onChange={(e) => setY(Number(e.target.value))}
            className="mt-2 w-full accent-amber-600"
          />
        </div>
        <div className="flex justify-between items-baseline border-t border-amber-200/70 pt-4">
          <span className="text-[11px] font-mono font-bold uppercase tracking-[0.14em] text-slate-500">{t("nqm.mosca.zLabel")}</span>
          <span className="font-mono font-extrabold text-slate-900">{z.toFixed(1)}y</span>
        </div>
      </div>

      <div data-testid="mosca-result" className="mt-5 p-4 rounded-2xl bg-white border border-slate-200">
        <p className="font-mono text-center text-lg font-extrabold text-slate-900 tabular-nums">
          {x} + {y} = {sum} <span className="text-slate-400 text-sm font-bold">{t("nqm.mosca.vs")}</span> Z = {z.toFixed(1)}
        </p>
        <div className={`mt-3 text-center text-[10px] font-mono font-bold tracking-[0.16em] border px-3 py-2 rounded-full ${
          atRisk ? "text-red-700 bg-red-50 border-red-300" : "text-emerald-700 bg-emerald-50 border-emerald-300"
        }`}>
          {atRisk ? t("nqm.mosca.risk") : t("nqm.mosca.safe")}
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-500 leading-relaxed">{t("nqm.mosca.note")}</p>
    </div>
  );
};

export const NqmHub = () => {
  const { t } = useLang();
  const [sector, setSector] = useState(0);
  const [checks, setChecks] = useState({});
  const [score, setScore] = useState(null);

  const compute = () => {
    const base = 14 + SECTOR_RISK[sector];
    const gained = POSTURE.reduce((acc, p) => acc + (checks[p.id] ? p.gain : 0), 0);
    setScore(Math.min(100, base + gained));
  };

  const verdict =
    score === null ? "" : score < 45 ? t("nqm.calc.critical") : score < 70 ? t("nqm.calc.transitioning") : t("nqm.calc.ready");
  const verdictColor = score < 45 ? "text-amber-700 bg-amber-50 border-amber-300" : score < 70 ? "text-blue-700 bg-blue-50 border-blue-300" : "text-emerald-700 bg-emerald-50 border-emerald-300";

  return (
    <section id="nqm" data-testid="nqm-section" className="py-20 lg:py-28 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow={t("nqm.eyebrow")}
          title={
            <>
              {t("nqm.titleA")}
              <span className="text-blue-700">{t("nqm.titleB")}</span>
            </>
          }
          sub={t("nqm.sub")}
        />

        <div className="relative mb-16">
          <div className="hidden lg:block absolute top-[26px] left-0 right-0 h-px bg-slate-200" />
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-4">
            {YEARS.map((year, i) => (
              <Reveal key={year} delay={i * 0.12} className="relative">
                <div data-testid={`nqm-timeline-${year}`} className="flex lg:flex-col items-start gap-4 lg:gap-0">
                  <div className="relative z-10 shrink-0 grid place-items-center w-[52px] h-[52px] rounded-full bg-white border-2 border-blue-600 font-mono font-extrabold text-blue-700 text-sm shadow-[0_0_0_6px_rgba(29,78,216,0.08)]">
                    {year.slice(2)}
                  </div>
                  <div className="lg:mt-5">
                    <p className="font-mono text-xs font-bold tracking-[0.18em] text-slate-400">{year}</p>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{t("nqm.timeline")[i]}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-5">
            {t("nqm.facts").map((f, i) => {
              const Icon = FACT_ICONS[i];
              return (
                <Reveal key={f.title} delay={i * 0.1}>
                  <div className="flex gap-5 p-6 rounded-2xl border border-slate-200/90 bg-slate-50/60 h-full">
                    <Icon className="w-6 h-6 text-blue-700 shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-base font-bold text-slate-900">{f.title}</h3>
                      <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div data-testid="readiness-calculator" className="h-full p-7 sm:p-8 rounded-3xl border border-blue-200 bg-gradient-to-b from-blue-50/70 to-white shadow-[0_20px_50px_-24px_rgba(29,78,216,0.35)]">
              <div className="flex items-center gap-3">
                <Gauge className="w-5 h-5 text-blue-700" />
                <h3 className="text-lg font-extrabold text-slate-900 tracking-tight">{t("nqm.calc.title")}</h3>
              </div>
              <p className="mt-2 text-sm text-slate-500">{t("nqm.calc.desc")}</p>

              <div className="mt-6">
                <label className="text-[11px] font-mono font-bold uppercase tracking-[0.18em] text-slate-500">{t("nqm.calc.sectorLabel")}</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {t("nqm.calc.sectors").map((s, i) => (
                    <button
                      key={s}
                      data-testid={`calc-sector-${i}`}
                      onClick={() => { setSector(i); setScore(null); }}
                      className={`text-xs font-bold px-3.5 py-2 rounded-full border transition-all duration-200 ${
                        sector === i ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-600 border-slate-300 hover:border-blue-400"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-5 space-y-2.5">
                {POSTURE.map((p, i) => (
                  <label key={p.id} data-testid={`calc-check-${p.id}`} className="flex items-center gap-3 text-sm text-slate-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!checks[p.id]}
                      onChange={(e) => { setChecks({ ...checks, [p.id]: e.target.checked }); setScore(null); }}
                      className="w-4 h-4 rounded border-slate-300 text-blue-700 focus:ring-blue-600"
                    />
                    {t("nqm.calc.posture")[i]}
                  </label>
                ))}
              </div>

              <button
                data-testid="qsd-calc-button"
                onClick={compute}
                className="mt-6 w-full text-sm font-bold text-white bg-slate-900 hover:bg-blue-700 py-3.5 rounded-full transition-colors duration-300"
              >
                {t("nqm.calc.button")}
              </button>

              {score !== null && (
                <motion.div data-testid="calc-result" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
                  <div className="flex items-end justify-between">
                    <p className="font-mono text-4xl font-extrabold text-slate-900">{score}<span className="text-base text-slate-400">/100</span></p>
                    <span className={`text-[10px] font-mono font-bold tracking-[0.16em] border px-3 py-1.5 rounded-full ${verdictColor}`}>{verdict}</span>
                  </div>
                  <div className="mt-3 h-2.5 rounded-full bg-slate-200 overflow-hidden">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-amber-500 via-blue-600 to-emerald-600"
                      initial={{ width: 0 }}
                      animate={{ width: `${score}%` }}
                      transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                  <p className="mt-3 text-xs text-slate-500">{t("nqm.calc.note")}</p>
                </motion.div>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <MoscaWidget />
          </Reveal>
        </div>
      </div>
    </section>
  );
};
